<template>
  <div class="component">
    <b-breadcrumb :items="items"></b-breadcrumb>
    <div class="container-fluid">
      <div class="row mb-3 align-items-center" style="margin-left: 20px; margin-right: 20px;">
        <div class="col-md-6">
          <div class="d-flex align-items-center">
            <span class="textGray mr-2">Buscar</span>
            <multiselect
              v-model="selectedMachineObject"
              :options="machines"
              track-by="id"
              label="name"
              placeholder="Pesquisar Máquinas"
              :searchable="true"
              :internal-search="false"
              :allow-empty="true"
              :clear-on-select="false"
              :close-on-select="true"
              class="flex-grow-1"
              @search-change="onSearchChange"
            >
            </multiselect>
          </div>
        </div>
        <div class="col-md-6 d-flex justify-content-end">
          <b-button
            variant="secondary"
            @click="exportToExcel"
            :disabled="loadingExport"
            class="btn-export-excel"
          >
            <font-awesome-icon icon="file-alt" class="mr-2" />
            {{ loadingExport ? 'Gerando...' : 'Exportar Máquinas(xlsx)' }}
          </b-button>
        </div>
      </div>
      <div class="d-flex align-items-center justify-content-end mb-2" style="margin-right: 20px;">
        <GalleryDownloadProgress
          v-if="downloadProgress"
          class="mr-auto"
          style="width: 420px; margin-left: 20px;"
          :label="`Baixando galeria: ${downloadingMachineName}`"
          :current="downloadProgress.current"
          :total="downloadProgress.total"
        />
        <label for="machinesPerPage" class="mr-2 mb-0">Itens por página</label>
        <select
          id="machinesPerPage"
          v-model.number="perPage"
          class="form-control"
          style="width: auto"
          @change="onPerPageChange(perPage)"
        >
          <option v-for="option in perPageOptions" :key="option" :value="option">{{ option }}</option>
        </select>
      </div>
        <b-table
          :fields="fields"
          :items="paginatedMachines"
          :filter="query"
          :filterIncludedFields="filterOn"
          @filtered="onFiltered"
          centered
        >
        <template v-slot:cell(created_at)="data">
          {{ formatDateTime(data.value) }}
        </template>
        <template v-slot:cell(last_updated)="data">
          {{ formatDateTime(data.value) }}
        </template>
        <template v-slot:cell(id)="data">
          <div>
            <b-button
              class="trash"
              @click="openModal('confirm-modal', data.value)"
              v-if="canAccess()"
            >
              <font-awesome-icon icon="trash" color="red" />
            </b-button>
            <b-button class="pencil" @click="goToMachineEdit(data.value, data.item.company)">
              <font-awesome-icon icon="pencil-alt" />
            </b-button>
            <b-dropdown
              variant="link"
              no-caret
              right
              toggle-class="text-dark text-decoration-none"
              :disabled="downloadProgress !== null"
            >
              <template v-slot:button-content>
                <font-awesome-icon icon="download" />
              </template>
              <b-dropdown-item @click="handleDownload(data, true)">Galeria HD</b-dropdown-item>
              <b-dropdown-item @click="handleDownload(data, false)">Galeria SD</b-dropdown-item>
            </b-dropdown>
          </div>
        </template>
      </b-table>
      <b-pagination
        class="float-right"
        v-model="currentPage"
        :total-rows="rows"
        :per-page="perPage"
        aria-controls="my-table"
      ></b-pagination>
    </div>
    <b-modal
      ref="confirm-modal"
      title="Excluir Máquina"
      modal-ok="Sim"
      modal-cancel="Não"
      @ok="removeMachine(machineToDelete.id)"
      :ok-disabled="!allowMachineDeletion"
      @hide="onMachineDeleteModalHide()"
    >
      <div v-if="loadingDocumentQuantities">
        <b-spinner variant="primary" label="Loading..."></b-spinner>
      </div>
      <div v-else>
        <span>Deseja excluir a máquina {{ machineToDelete?.name }}?</span>

        <div v-if="machineHasDocuments" class="mt-2 card bg-grey">
          <div class="d-flex flex-wrap align-items-center">
            <div class="d-flex p-3">
              <div class="icon mr-2">
                <font-awesome-icon icon="list" />
              </div>
              <div>
                Inventário
                <br />
                <span class="number">
                  <b>{{ machineDocumentQuantities.inventories }}</b></span
                >
              </div>
            </div>
            <div class="d-flex p-3">
              <div class="icon mr-2">
                <font-awesome-icon icon="hand-paper" />
              </div>
              <div>
                APR
                <br />
                <span class="number">
                  <b>{{ machineDocumentQuantities.hazard_analysis }}</b></span
                >
              </div>
            </div>
            <div class="d-flex p-3">
              <div class="icon mr-2">
                <font-awesome-icon icon="cog" />
              </div>
              <div>
                Plano de Ação
                <br />
                <span class="number">
                  <b>{{ machineDocumentQuantities.action_plans }}</b></span
                >
              </div>
            </div>
            <div class="d-flex p-3">
              <div class="icon mr-2">
                <font-awesome-icon icon="eye-slash" />
              </div>
              <div>
                Diagnóstico
                <br />
                <span class="number">
                  <b>{{ machineDocumentQuantities.nr_references }}</b></span
                >
              </div>
            </div>
            <div class="d-flex p-3">
              <div class="icon mr-2">
                <font-awesome-icon icon="shield-alt" />
              </div>
              <div>
                DVS
                <br />
                <span class="number">
                  <b>{{
                    machineDocumentQuantities.security_validations
                  }}</b></span
                >
              </div>
            </div>
            <div>
              <router-link
                class="d-flex p-3"
                :to="{
                  name: 'Company_Documents',
                  params: {
                    company_id: companyIdSelectedMachine,
                  },
                  query: {
                    ['machine_id']: machineToDelete.id,
                  },
                }"
              >
                <div class="icon mr-2">
                  <font-awesome-icon icon="file-alt" />
                </div>
                <div>Ver Documentos</div>
              </router-link>
            </div>
          </div>
          <b-form-checkbox
            class="m-3"
            id="confirmMachineDeletionDespiteDocuments"
            v-model="confirmMachineDeletionDespiteDocuments"
            name="confirmMachineDeletionDespiteDocuments"
          >
            Confirmar a exclusão da máquina e seus documentos associados?
          </b-form-checkbox>
        </div>
        <div v-else><i>nenhum documento associado</i></div>
      </div>
      <template v-slot:modal-cancel>Cancelar</template>
      <template v-slot:modal-ok>Confirmar</template>
    </b-modal>
    <br />
    <br />
  </div>
</template>

<script>
import { postDocumentsQuantity } from "@/api/admin/company";
import axios from "@/api/axios";
import { types as machineTypes } from "@/store/modules/machine";
import { canAccess } from "@/utils/canAccess";
import { downloadGallery, getHdFallbackNotice } from "@/utils/downloadGallery";
import GalleryDownloadProgress from "@/components/GalleryDownloadProgress.vue";
import Multiselect from 'vue-multiselect';
import { mapMutations } from "vuex/dist/vuex.common.js";

const PER_PAGE_OPTIONS = [10, 20, 50, 100];
const PER_PAGE_STORAGE_KEY = 'machines_per_page';

function loadStoredPerPage() {
  const stored = Number(localStorage.getItem(PER_PAGE_STORAGE_KEY));
  return PER_PAGE_OPTIONS.includes(stored) ? stored : PER_PAGE_OPTIONS[0];
}

export default {
  name: "Machines_List",
  components: {
    Multiselect,
    GalleryDownloadProgress
  },
  computed: {
    query: {
      get() {
        return this.$store.state[machineTypes.MODULE][machineTypes.QUERY];
      },
      set(value) {
        this.setQuery(value);
      },
    },
    allowMachineDeletion() {
      return (
        this.confirmMachineDeletionDespiteDocuments || !this.machineHasDocuments
      );
    },
    isAllMachinesSelected() {
      return this.selectedMachine === "0" || this.selectedMachine === 0;
    },
    machineHasDocuments() {
      const machinesCount = this.machineDocumentQuantities?.machines ?? 0;
      const totalCount = this.machineDocumentQuantities?.total ?? 0;
      const realTotalCount = totalCount - machinesCount;
      return realTotalCount > 0;
    },
    selectedCompanyId() {
      return this.$route.params.company_id || localStorage.getItem('company_id') || null;
    },
    paginatedMachines() {
      const start = (this.currentPage - 1) * this.perPage;
      return this.filteredMachines.slice(start, start + this.perPage);
    }
  },
  data() {
    return {
      localCompanyId: localStorage.getItem('company_id'),
      corporateGroupId: null,
      confirmMachineDeletionDespiteDocuments: false,
      loadingDocumentQuantities: false,
      loadingExport: false,
      downloadProgress: null,
      downloadingMachineName: "",
      perPage: loadStoredPerPage(),
      perPageOptions: PER_PAGE_OPTIONS,
      currentPage: 1,
      rows: 4,
      allMachines: [], // base inteira (por empresa, se houver company_id), carregada uma vez
      filteredMachines: [],
      dismissSecs: 5,
      dismissCountDown: 0,
      filterOn: [],
      search: "",

      items :[
        { text: "Documentos", href: "#/admin/Documents/Index" },
        ...(localStorage.getItem("company_id")
          ? [{
              text: localStorage.getItem("companyName"),
              href: `#/admin/Documents/Company/${localStorage.getItem("company_id")}`,
            }]
          : []),
        { text: "Máquinas", active: true },

        ...(canAccess()
          ? [{ text: "Nova", href: "#/admin/Machines/New" }]
          : []),
      ],
      fields: [
        { key: "name", label: "Nome" },
        { key: "sector", label: "Setor" },
        { key: "company.corporate_name", label: "Unidade" },
        { key: "created_at", label: "Criado em" },
        { key: "last_updated", label: "Modificado em" },
        { key: "id", label: "Ações" },
      ],
      role: localStorage.getItem("auth.role"),
      email: localStorage.getItem("auth.email"),
      machineToDelete: null,
      companyIdSelectedMachine: null,
      machineDocumentQuantities: null,
      selectedMachineId: null,
      selectedMachine: "0",
      selectedMachineObject: null,
      machines: [],
      company: null,
    };
  },
  mounted: async function() {
    if (this.selectedCompanyId) {
      this.getCompany(this.selectedCompanyId);
    }
    await this.loadAllMachines();
    this.checkSuccessNotification();
  },
  methods: {
    canAccess,
    getCompany: function (company_id) {
      axios
        .get(`${process.env.VUE_APP_API_HOST}/api/admin/companies/${company_id}`)
        .then((response) => {
          const company = response.data.data;
          this.corporateGroupId = company.corporate_group_id;

          const companyItem = this.items.find(
            (item) => item.href && item.href.startsWith('#/admin/Documents/Company/')
          );
          if (companyItem) {
            companyItem.text = company.corporate_name;
          }

          const novaItem = this.items.find((item) => item.text === 'Nova');
          if (novaItem) {
            novaItem.href = `#/admin/Machines/New?corporate-group-id=${this.corporateGroupId}`;
          }
        });
    },
    goToMachineEdit(machineId, company) {
      // Faz a navegação igual ao <router-link>
      this.$router.push({
        name: "Machines_New",
        params: { id: machineId },
        query: { "company-id": company.id }
      });
    },
    formatDateTime(dateStr) {
      if (!dateStr) return '-';
      const d = new Date(dateStr.replace(' ', 'T'));
      const pad = n => n.toString().padStart(2, '0');
      return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
    },
    checkSuccessNotification() {
      if (localStorage.getItem('machine_success_notification') === 'true') {
        this.$bvToast.toast(
          'Máquina salva com sucesso!',
          {
            title: 'Sucesso',
            toaster: "b-toaster-top-right",
            solid: true,
            variant: "success",
            autoHideDelay: 5000
          }
        );
        
        localStorage.removeItem('machine_success_notification');
      }
    },
    ...mapMutations(machineTypes.MODULE, {
      setQuery: machineTypes.SET_QUERY,
    }),
    onMachineDeleteModalHide() {
      this.machineToDelete = null;
      this.companyIdSelectedMachine = null;
      this.machineDocumentQuantities = null;
      this.confirmMachineDeletionDespiteDocuments = false;
    },

    // Único lugar que decide se uma máquina bate com o termo buscado — usado tanto
    // pra filtrar a tabela quanto pra montar as sugestões do combo, pra não correr
    // o risco de as duas coisas responderem campos diferentes (foi esse descompasso,
    // entre filtro do front e busca do backend, que gerou o card #360).
    matchesSearch(machine, term) {
      const query = term.toLowerCase();
      const name = machine.name?.toLowerCase() ?? '';
      const code = machine.code?.toLowerCase() ?? '';
      return name.includes(query) || code.includes(query);
    },

    // Carrega a base inteira de uma vez só (endpoint cacheado no backend, ver #351),
    // pra o combo já nascer com sugestões de todas as máquinas, não só da página atual.
    async loadAllMachines() {
      const loader = this.$loading.show();
      try {
        // Endpoint enxuto (só nome/código/setor/unidade/datas — o que essa tela usa),
        // não a listagem completa: essa tinha 14,8 MB pra ~4.400 máquinas em QA por
        // carregar photo/address da empresa e photo da própria máquina sem necessidade.
        const response = await axios.get(
          `${process.env.VUE_APP_API_HOST}/api/admin/machines/lean`,
          {
            params: {
              company_id: this.localCompanyId,
            },
          }
        );
        this.allMachines = response.data.data;
        this.applyFilter();
      } catch (error) {
        console.error('Erro ao carregar máquinas:', error);
      } finally {
        loader.hide();
      }
    },

    applyFilter() {
      this.filteredMachines = this.search
        ? this.allMachines.filter(machine => this.matchesSearch(machine, this.search))
        : this.allMachines;
      this.rows = this.filteredMachines.length;

      this.machines = [
        { id: "0", name: "Todas as Máquinas" },
        ...this.filteredMachines
      ];
    },

    // Chamado a cada tecla digitada no combo. Como a base inteira já está carregada,
    // o filtro é instantâneo, sem ida ao servidor.
    onSearchChange(term) {
      if (!term) {
        // O vue-multiselect dispara search-change com string vazia ao perder o foco
        // (limpeza interna do próprio campo) — não é o usuário pedindo pra ver todas
        // de novo. Ignora; pra isso existe a opção "Todas as Máquinas" no combo.
        return;
      }

      this.search = term;
      this.currentPage = 1;
      this.applyFilter();
    },

    onPerPageChange(value) {
      localStorage.setItem(PER_PAGE_STORAGE_KEY, value);
      this.currentPage = 1;
    },

    updateSelectedMachineObject: function() {
      if (this.selectedMachine === "0" || this.selectedMachine === 0) {
        this.selectedMachineObject = { id: "0", name: "Todas as Máquinas" };
      } else if (this.company && this.company.machines) {
        const machine = this.company.machines.find(m => m.id.toString() === this.selectedMachine.toString());
        if (machine) {
          this.selectedMachineObject = { id: machine.id.toString(), name: machine.name };
        }
      }
    },

    onFiltered(filteredItems) {
      this.totalRows = filteredItems.length;
    },
    countDownChanged(dismissCountDown) {
      this.dismissCountDown = dismissCountDown;
    },
    openModal(modal, machineId) {
      const machine = this.allMachines.find((machine) => machine.id === machineId);
      this.machineToDelete = machine;
      this.companyIdSelectedMachine = machine?.company_id;
      this.loadDocumentsQuantity();
      this.$refs[modal].show();
    },
    async loadDocumentsQuantity() {
      if (this.loadingDocumentQuantities) return;
      try {
        this.loadingDocumentQuantities = true;
        const res = await postDocumentsQuantity({
          companyId: this.companyIdSelectedMachine,
          machineId: this.machineToDelete.id,
        });
        this.machineDocumentQuantities = res.data;
      } catch (error) {
        this.$bvToast.toast(
          error?.response?.data?.message ??
            "Erro ao carregar quantidade de documentos",
          {
            title: "Ocorreu um erro",
            variant: "danger",
            solid: true,
          }
        );
      } finally {
        this.loadingDocumentQuantities = false;
      }
    },
    async removeMachine(machineId) {
      await axios.delete(
        `${process.env.VUE_APP_API_HOST}/api/admin/machines/${machineId}`
      );
      this.allMachines = this.allMachines.filter((el) => el.id != machineId);
      this.applyFilter();
    },

    async handleDownload(data, hd) {
      this.downloadingMachineName = data.item.name;
      this.downloadProgress = { current: 0, total: 0 };
      try {
        const response = await axios.get(
          `${process.env.VUE_APP_API_HOST}/api/admin/machines/${data.value}`,
          {
            params: {
              include: 'photo_gallery',
            },
          }
        );
        const machine = response.data.data;
        const summary = await downloadGallery(machine.photo_gallery, `${machine.name}`, {
          hd,
          onProgress: (progress) => {
            this.downloadProgress = progress;
          },
        });
        const notice = getHdFallbackNotice(summary);
        if (notice) {
          this.$bvToast.toast(notice, {
            title: "Download da galeria",
            toaster: "b-toaster-top-center",
            solid: true,
            variant: "warning",
            autoHideDelay: 8000,
          });
        }
      } finally {
        this.downloadProgress = null;
      }
    },

    async exportToExcel() {
      try {
        this.loadingExport = true;
        
        const response = await axios.get(
          `${process.env.VUE_APP_API_HOST}/api/export/machines`,
          {
            params: {
              company_id: this.selectedCompanyId,
              filter: this.query
            },
            responseType: 'blob'
          }
        );

        // Criar link para download
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `maquinas_${new Date().toISOString().split('T')[0]}.xlsx`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);

        this.$bvToast.toast(
          'Excel gerado com sucesso!',
          {
            title: 'Sucesso',
            toaster: "b-toaster-top-right",
            solid: true,
            variant: "success",
            autoHideDelay: 3000
          }
        );
      } catch (error) {
        console.error('Erro ao exportar:', error);
        this.$bvToast.toast(
          'Erro ao gerar o Excel. Tente novamente.',
          {
            title: 'Erro',
            toaster: "b-toaster-top-right",
            solid: true,
            variant: "danger",
            autoHideDelay: 5000
          }
        );
      } finally {
        this.loadingExport = false;
      }
    },
  },
  watch: {
    selectedMachine: {
      handler() {
        this.updateSelectedMachineObject();
      },
      immediate: true
    },
    company: {
      handler() {
        this.updateSelectedMachineObject();
      }
    },
    selectedMachineObject: {
      handler(newVal) {
        if (!newVal || newVal.id === "0") {
          this.selectedMachine = "0";
          this.search = "";
          this.currentPage = 1;
          this.applyFilter();
          return;
        }

        this.selectedMachine = newVal.id;

        // Se a máquina escolhida já veio na última busca, só restringe a tabela a ela.
        const picked = this.allMachines.find(machine => machine.id === newVal.id);
        if (picked) {
          this.filteredMachines = [picked];
          this.rows = 1;
          this.currentPage = 1;
        }
      }
    },
    "$route.query.machine_id": {
      handler(newVal) {
        if (newVal) {
          const machine = this.machines.find(m => m.id === newVal);
          if (machine) {
            this.selectedMachine = machine.id;
            this.selectedMachineObject = {
              id: machine.id,
              name: machine.name,
            };
          }
        }
      },
      immediate: true
    }
  },
};
</script>
<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>
<style>
.pencil {
  color: #007bff;
  background-color: #ffffff;
}
.pencil:hover {
  color: #007bff !important;
  background-color: #ffffff;
}
.btn-secondary {
  border: none !important;
  box-shadow: none !important;
}
.btn-secondary:hover {
  color: #ffffff;
  box-shadow: none !important;
}
.trash {
  background-color: #ffffff !important;
}

.btn-export-excel {
  background-color: #6c757d !important;
  border-color: #6c757d !important;
  color: white !important;
  font-weight: 500;
  padding: 8px 16px;
}

.btn-export-excel:hover {
  background-color: #5a6268 !important;
  border-color: #545b62 !important;
  color: white !important;
}

.btn-export-excel:disabled {
  background-color: #6c757d !important;
  border-color: #6c757d !important;
  opacity: 0.65;
}
</style>
